import React, { useState } from "react";
import { Collapse, Popover, PopoverBody, Table } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import * as SweetAlert from "sweetalert2";

import { sendFrasersReminder } from "../../store/orderSlice";

/**
 * Status cell, same treatment as the order page's Email History: green text
 * when sent, red text with an info icon and a hover popover carrying the
 * error when not.
 */
const ReminderStatus = ({ row }) => {
    const [open, setOpen] = useState(false);
    const id = `fp-reminder-status-${row.id}`;

    if (row.status === "sent") {
        return <p className="text-success mb-0">Successfully Sent</p>;
    }

    return (
        <>
            <p className="text-danger mb-0" id={id}>
                Failed{" "}
                <span style={{ cursor: "pointer", marginLeft: "6px" }}>
                    <i className="fa fa-info-circle" aria-hidden="true"></i>
                </span>
            </p>
            <Popover placement="bottom" isOpen={open} target={id} toggle={() => setOpen(!open)} trigger="hover">
                <PopoverBody>
                    <pre className="mb-0" style={{ whiteSpace: "pre-wrap" }}>{row.error || "No error detail recorded."}</pre>
                </PopoverBody>
            </Popover>
        </>
    );
};

/**
 * Frasers Plus payment status for this order, the "Send payment reminder"
 * button, and the history of every reminder email, in the same table style
 * as the Email History section further down the page.
 *
 * Rendered inside the Items card, above the invoice/refund buttons. Shown
 * only for orders placed with Frasers Plus; the button only while the order
 * is still pending.
 */
export const FrasersReminders = (props) => {
    const { orderState, orderId } = props;
    const dispatch = useDispatch();
    const userState = useSelector(({ authReducer }) => authReducer.data);
    const [sending, setSending] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);

    const order = orderState.data;
    const gateway = order && order.payment_gateway_name ? String(order.payment_gateway_name) : "";

    if (!order || !/frasers/i.test(gateway) || (userState.user && userState.user.role.name === "Seller")) {
        return null;
    }

    const pending = order.financial_status === "pending" && !order.is_canceled;
    const reminders = orderState.frasersReminders || [];
    const sentCount = reminders.filter((r) => r.status === "sent").length;
    const colour = pending ? "#dc3545" : "#28a745";

    const confirmAndSend = () => {
        SweetAlert.fire({
            title: "Send payment reminder?",
            text: `The customer will receive an email with the "Pay with Frasers Plus" link for order ${order.order_name}.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Send",
            cancelButtonText: "Cancel",
            reverseButtons: true,
        }).then((result) => {
            if (!result.value) return;
            setSending(true);
            dispatch(sendFrasersReminder(orderId)).finally(() => setSending(false));
        });
    };

    return (
        <div className="mb-3">
            <div
                className="py-2 px-3 rounded d-flex align-items-center flex-wrap"
                style={{ gap: 12, fontSize: 13, background: "#ffffff", border: `1px solid ${colour}`, color: colour }}
            >
                <div className="flex-grow-1">
                    <i className={`fa ${pending ? "fa-clock-o" : "fa-check-circle"} mr-2`}></i>
                    <strong>Frasers Plus:</strong>{" "}
                    {pending ? "Awaiting payment" : <span className="text-capitalize">{order.financial_status}</span>}
                    <span className="mx-2">·</span>
                    <strong>Reminders sent:</strong> {sentCount}
                </div>
                {pending && (
                    <button
                        type="button"
                        onClick={confirmAndSend}
                        disabled={sending}
                        className={sending ? "btn btn-danger btn-sm text-white disabled" : "btn btn-danger btn-sm text-white"}
                    >
                        {sending ? <i className="fa fa-spinner fa-spin" aria-hidden="true"></i> : "Send payment reminder"}
                    </button>
                )}
            </div>

            {reminders.length > 0 && (
                <div className="mt-2">
                    <button
                        type="button"
                        className="btn btn-link p-0 d-flex align-items-center"
                        style={{ fontSize: 13, textDecoration: "none" }}
                        onClick={() => setHistoryOpen(!historyOpen)}
                        aria-expanded={historyOpen}
                    >
                        <i className={`fa ${historyOpen ? "fa-angle-down" : "fa-angle-right"} mr-2`} style={{ width: 10 }}></i>
                        Payment reminder history ({reminders.length})
                    </button>
                    <Collapse isOpen={historyOpen}>
                    <div className="table-responsive mt-2">
                        <Table size="sm" className="mb-0">
                            <thead>
                                <tr>
                                    <th>Email Sent to</th>
                                    <th>Reminder</th>
                                    <th>Sent on</th>
                                    <th>Sent by</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reminders.map((r) => (
                                    <tr key={r.id}>
                                        <td>{r.toEmail || "-"}</td>
                                        <td>{r.label}</td>
                                        <td>{r.sentAt}</td>
                                        <td>{r.sentBy}</td>
                                        <td><ReminderStatus row={r} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                    </Collapse>
                </div>
            )}
        </div>
    );
};
